import React, { useState } from 'react';

// Dependency-free SVG charts for the HR Payroll dashboard, matching this
// codebase's existing convention (see CashFlowChart.js) of plain SVG rather
// than a charting library.

const money = (n) => `₹${Number(n || 0).toLocaleString('en-IN')}`;
const moneyShort = (n) => {
  const v = Number(n || 0);
  const abs = Math.abs(v);
  if (abs >= 10000000) return `₹${(v / 10000000).toFixed(1)}Cr`;
  if (abs >= 100000) return `₹${(v / 100000).toFixed(1)}L`;
  if (abs >= 1000) return `₹${(v / 1000).toFixed(0)}K`;
  return money(v);
};

const W = 720;
const PAD_L = 60;
const PAD_R = 12;

const ChartTooltip = ({ pos, children }) => {
  if (!pos) return null;
  return (
    <div
      className="absolute z-10 pointer-events-none bg-gray-900 text-white text-xs rounded px-2.5 py-2 shadow-lg whitespace-nowrap"
      style={{ left: `${pos.leftPct}%`, top: `${pos.topPct}%`, transform: 'translate(-50%, -100%) translateY(-8px)' }}
    >
      {children}
    </div>
  );
};

const EmptyState = ({ label }) => (
  <p className="text-sm text-gray-400 py-10 text-center">{label || 'No data for this view.'}</p>
);

/**
 * One or two lines over a category axis. points: [{label, ...values}].
 * series: [{key, label, color}]. valueFormatter defaults to moneyShort -
 * pass an identity function for plain-number series (counts/hours).
 */
export const TrendLineChart = ({ points, series, valueFormatter = moneyShort, emptyLabel }) => {
  // Hooks must run on every render regardless of the empty-state early
  // return below (react-hooks/rules-of-hooks) - declared first, unused
  // when there's nothing to plot.
  const [hover, setHover] = useState(null);
  if (!points || points.length === 0) return <EmptyState label={emptyLabel} />;

  const H = 210;
  const PAD_T = 22;
  const PAD_B = 26;
  const innerW = W - PAD_L - PAD_R;
  const innerH = H - PAD_T - PAD_B;
  const n = points.length;

  const allValues = series.flatMap((s) => points.map((p) => p[s.key] || 0));
  let min = Math.min(0, ...allValues);
  let max = Math.max(...allValues, 1);
  let span = (max - min) || 1;
  max += span * 0.12;
  span = max - min;

  const x = (i) => (n <= 1 ? PAD_L + innerW / 2 : PAD_L + (innerW * i) / (n - 1));
  const y = (v) => PAD_T + innerH - ((v - min) / span) * innerH;
  const gridValues = [0, 1, 2, 3].map((g) => min + (span * g) / 3);
  const tickStep = n > 8 ? Math.ceil(n / 7) : 1;
  const hoverP = hover != null ? points[hover] : null;

  return (
    <div className="relative">
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto" style={{ overflow: 'visible' }} onMouseLeave={() => setHover(null)}>
        {gridValues.map((gv, i) => (
          <g key={i}>
            <line x1={PAD_L} x2={W - PAD_R} y1={y(gv)} y2={y(gv)} stroke="#eef0f2" strokeWidth="1" />
            <text x={2} y={y(gv) + 3} fontSize="10" fill="#9ca3af">{valueFormatter(gv)}</text>
          </g>
        ))}
        <line x1={PAD_L} x2={W - PAD_R} y1={y(0)} y2={y(0)} stroke="#c8ccd1" strokeWidth="1" />

        {series.map((s) => {
          const linePoints = points.map((p, i) => `${x(i)},${y(p[s.key] || 0)}`).join(' L ');
          return (
            <g key={s.key}>
              <path d={`M ${linePoints}`} fill="none" stroke={s.color} strokeWidth="2" />
              {points.map((p, i) => (
                <circle key={i} cx={x(i)} cy={y(p[s.key] || 0)} r="3" fill="#fff" stroke={s.color} strokeWidth="2" />
              ))}
            </g>
          );
        })}

        {points.map((p, i) => (
          i % tickStep === 0 && (
            <text key={i} x={x(i)} y={H - 6} fontSize="10" fill="#9ca3af" textAnchor="middle">{p.label}</text>
          )
        ))}

        {hover != null && (
          <line x1={x(hover)} x2={x(hover)} y1={PAD_T} y2={H - PAD_B} stroke="#9ca3af" strokeWidth="1" strokeDasharray="2 2" />
        )}
        {points.map((p, i) => (
          <rect key={i} x={x(i) - (innerW / Math.max(n - 1, 1)) / 2} y={0} width={innerW / Math.max(n - 1, 1) || innerW} height={H} fill="transparent"
                onMouseEnter={() => setHover(i)} onMouseMove={() => setHover(i)} />
        ))}
      </svg>
      <ChartTooltip pos={hover != null ? { leftPct: (x(hover) / W) * 100, topPct: (PAD_T / H) * 100 } : null}>
        <div className="font-semibold mb-1">{hoverP?.label}</div>
        {series.map((s) => (
          <div key={s.key} className="flex justify-between gap-3">
            <span style={{ color: s.color }}>{s.label}</span><b>{valueFormatter(hoverP?.[s.key] || 0)}</b>
          </div>
        ))}
      </ChartTooltip>
    </div>
  );
};

/**
 * Grouped/simple vertical bars over a category axis. data: [{label, ...values}].
 * series: [{key, label, color}] - one bar per series per category, side by side.
 */
export const GroupedBarChart = ({ data, series, valueFormatter = moneyShort, emptyLabel }) => {
  // See TrendLineChart's comment above - same rules-of-hooks fix.
  const [hover, setHover] = useState(null);
  if (!data || data.length === 0) return <EmptyState label={emptyLabel} />;

  const H = 210;
  const PAD_T = 16;
  const PAD_B = 30;
  const innerW = W - PAD_L - PAD_R;
  const innerH = H - PAD_T - PAD_B;
  const n = data.length;

  const maxVal = Math.max(1, ...data.flatMap((d) => series.map((s) => d[s.key] || 0))) * 1.15;
  const slot = innerW / n;
  const groupW = Math.min(slot * 0.7, 56);
  const barW = groupW / series.length;
  const y = (v) => PAD_T + innerH - (v / maxVal) * innerH;
  const hoverD = hover != null ? data[hover] : null;

  return (
    <div className="relative">
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto" style={{ overflow: 'visible' }} onMouseLeave={() => setHover(null)}>
        <line x1={PAD_L} x2={W - PAD_R} y1={y(0)} y2={y(0)} stroke="#c8ccd1" strokeWidth="1" />
        <text x={2} y={y(0) + 3} fontSize="10" fill="#9ca3af">0</text>
        <text x={2} y={y(maxVal) + 3} fontSize="10" fill="#9ca3af">{valueFormatter(maxVal)}</text>
        <line x1={PAD_L} x2={W - PAD_R} y1={y(maxVal)} y2={y(maxVal)} stroke="#eef0f2" strokeWidth="1" />

        {data.map((d, i) => {
          const groupX = PAD_L + slot * i + (slot - groupW) / 2;
          return (
            <g key={i}>
              {series.map((s, si) => {
                const val = d[s.key] || 0;
                const barX = groupX + si * barW;
                const barH = Math.max((val / maxVal) * innerH, val > 0 ? 1 : 0);
                return (
                  <rect key={s.key} x={barX + 1} y={y(val)} width={Math.max(barW - 2, 1)} height={barH} rx="2" fill={s.color} />
                );
              })}
              <text x={PAD_L + slot * i + slot / 2} y={H - 8} fontSize="10" fill="#9ca3af" textAnchor="middle">
                {d.label.length > 12 ? `${d.label.slice(0, 11)}…` : d.label}
              </text>
            </g>
          );
        })}

        {data.map((d, i) => (
          <rect key={i} x={PAD_L + slot * i} y={0} width={slot} height={H} fill="transparent"
                onMouseEnter={() => setHover(i)} onMouseMove={() => setHover(i)} />
        ))}
        {hover != null && (
          <line x1={PAD_L + slot * hover + slot / 2} x2={PAD_L + slot * hover + slot / 2} y1={PAD_T} y2={H - PAD_B}
                stroke="#9ca3af" strokeWidth="1" strokeDasharray="2 2" opacity="0.5" />
        )}
      </svg>
      <ChartTooltip pos={hover != null ? { leftPct: ((PAD_L + slot * hover + slot / 2) / W) * 100, topPct: (PAD_T / H) * 100 } : null}>
        <div className="font-semibold mb-1">{hoverD?.label}</div>
        {series.map((s) => (
          <div key={s.key} className="flex justify-between gap-3">
            <span style={{ color: s.color }}>{s.label}</span><b>{valueFormatter(hoverD?.[s.key] || 0)}</b>
          </div>
        ))}
      </ChartTooltip>
    </div>
  );
};

export const identity = (n) => Number(n || 0).toLocaleString('en-IN');
export { money, moneyShort };
